import React, { ReactNode, useContext, useEffect, useState } from "react";
import PlaylistModalContext from "./PlaylistModalContext";
import callApi from "../../components/call-api/call-api";
import { Artist } from "../../types/Artist";
import { Album } from "../../types/Album";
import { Track } from "../../types/Track";
import { Playlist } from "../../types/Playlist";
import { deletePlaylistItem, getPlaylist, getPlaylists, getTracks, postPlaylistItem } from "../../components/apis/music-api";
import { AuthenticationContext } from "../../components/authentication/AuthenticationContext";
import { AddPlaylistItem } from "../../types/AddPlaylistItem";
import { getLetter } from "../../components/text-helper/text-helper";
import { doesTrackMatchPlaylistItem } from "./playlist-modal-helper";

interface PlaylistModalProviderProps {
    children: ReactNode;
}

const PlaylistModalProvider = (props: PlaylistModalProviderProps) => {
    const { getAccessToken } = useContext(AuthenticationContext);
    const [ playlistModalOpen, setPlaylistModalOpen ] = useState<boolean>(false);    
    const [ playlists, setPlaylists ] = useState<Playlist[]>([]);

    const [ selectedPlaylistIds, setSelectedPlaylistIds ] = useState<string[]>([]);
    const [ originalSelectedPlaylistIds, setOriginalSelectedPlaylistIds ] = useState<string[]>([]);

    const [ artist, setArtist ] = useState<Artist | undefined>(undefined);
    const [ album, setAlbum ] = useState<Album | undefined>(undefined);
    const [ track, setTrack ] = useState<Track | undefined>(undefined);
    
    useEffect(() => {
        const selectedIds: string[] = [];
        playlists.forEach(playlist => {
            if (playlist.playlistItems && track) {
                const exists = playlist.playlistItems.some(item => doesTrackMatchPlaylistItem(item, artist, album, track));
                if (exists) {
                    selectedIds.push(playlist.id);
                }
            }
        });

        setSelectedPlaylistIds(selectedIds);
        setOriginalSelectedPlaylistIds(selectedIds);
    }, [artist, album, track, playlists]);

    const applyItemToPlaylists = async (artist: Artist | undefined, album: Album | undefined, track: Track | undefined) => {
        await callApi<Playlist[]>(getPlaylists(getAccessToken()), setPlaylists);
        setArtist(artist);
        setAlbum(album);
        setTrack(track);
        setPlaylistModalOpen(true);
    }

    const handleTrackPlaylistChange = (selected: boolean, playlistId: string) => {
        if (selected) {
            setSelectedPlaylistIds([...selectedPlaylistIds, playlistId]);
        } else {
            setSelectedPlaylistIds(selectedPlaylistIds.filter(id => id !== playlistId));
        }
    }

    const getAlbumDuration = async (album: Album | undefined) : Promise<number> => {
        const tracks = await getTracks(getAccessToken(), album?.artistName || '', album?.year.toString() + ' - ' + album?.title || '');
        const sum = tracks.reduce((accumulator: number, track) => accumulator + (track.duration ?? 0), 0);
        return sum;
    }
    
    const handleSave = async () => {
        await Promise.all(playlists?.map(async (playlist) => {
            if (selectedPlaylistIds.includes(playlist.id)) {
                // Add to playlist
                if (!playlist.playlistItems) {
                    playlist.playlistItems = [];
                }

                const addPlaylistItem : AddPlaylistItem = track?.name ? {
                    isCompilation: false,
                    letter: getLetter(artist?.name || ''),
                    artistName: artist?.name || 'Unknown Artist',
                    albumYear: album?.year,
                    albumTitle: album?.title,
                    trackNumber: track?.number,
                    trackName: track?.name,
                    duration: track?.duration,
                } : {
                    isCompilation: false,
                    letter: getLetter(artist?.name || ''),
                    artistName: artist?.name || 'Unknown Artist',
                    albumYear: album?.year,
                    albumTitle: album?.title,
                    duration: await getAlbumDuration(album),
                }

                await postPlaylistItem(getAccessToken(), playlist.id, addPlaylistItem);
            } else {
                // Remove from playlist
                const foundId = originalSelectedPlaylistIds.find(id => id === playlist.id) ?? '';
                if (track && foundId) {
                    const p = await getPlaylist(getAccessToken(), foundId);
                    if (!p.playlistItems) {
                        return;
                    }

                    const item = p.playlistItems.find(item => doesTrackMatchPlaylistItem(item, artist, album, track));
                    if (item) {
                        await deletePlaylistItem(getAccessToken(), foundId, item.number);
                    }
                }
            }
        }));

        setPlaylistModalOpen(false);
    }

    return (
        <PlaylistModalContext.Provider value={{
            playlistModalOpen,
            setPlaylistModalOpen,
            playlists,
            artist,
            album,
            track,
            selectedPlaylistIds,
            applyItemToPlaylists,
            handleTrackPlaylistChange,
            handleSave,
        }}>
            {props.children}
        </PlaylistModalContext.Provider>
    );
}

export default PlaylistModalProvider;
